<template>
  <Container
    v-if="structuredTable || isEditing"
    :id="id"
    class="mt-16 first:mt-0 lg:mt-36"
  >
    <figure>
      <figcaption
        v-if="structuredTable?.caption && !options.hideCaption"
        class="mb-10 text-lg font-semibold tracking-wide lg:mb-32 lg:text-2xl"
        v-html="structuredTable.caption"
      />
      <StructuredTable
        v-if="filteredStructuredTable"
        v-bind="filteredStructuredTable"
        :id="uuid"
        :mobile-style="options.mobileStyle"
        :sticky="options.sticky"
        :equalize-widths="options.equalizeWidths"
      />
    </figure>
  </Container>
</template>

<script lang="ts" setup>
import type { ParagraphTableFragment } from '#graphql-operations'
import { filterNullArray } from '~/helpers'

const { options, uuid } = defineBlokkli({
  bundle: 'table',
  globalOptions: ['anchorId'],
  options: {
    hideCaption: {
      type: 'checkbox',
      label: 'Beschriftung verstecken',
      default: false,
    },
    equalizeWidths: {
      type: 'checkbox',
      label: 'Alle Spalten gleich breit',
      default: true,
    },
    mobileStyle: {
      type: 'radios',
      label: 'Mobile Darstellung',
      default: 'horizontal',
      displayAs: 'icons',
      options: {
        vertical: {
          label: 'Vertikal',
          icon: 'icon-blokkli-option-table-vertical',
        },
        horizontal: {
          label: 'Horizontal',
          icon: 'icon-blokkli-option-table-horizontal',
        },
      },
    },
    sticky: {
      type: 'radios',
      label: 'Sticky',
      default: 'left',
      options: {
        none: '-',
        left: 'Links',
        top: 'Oben',
      },
    },
  },
  editor: {
    editTitle: (el) => el.querySelector('figcaption')?.innerText,
    determineVisibleOptions: (ctx) => {
      if (ctx.options.mobileStyle === 'vertical') {
        return ['equalizeWidths', 'mobileStyle']
      }

      return ['equalizeWidths', 'mobileStyle', 'sticky']
    },
  },
})
const isEditing = false

const id = useAnchorId(options)

const props = defineProps<{
  structuredTable?: ParagraphTableFragment['structuredTable']
}>()

const filteredStructuredTable = computed(() => {
  if (!props.structuredTable) return undefined

  return {
    ...props.structuredTable,
    thead: filterNullArray(props.structuredTable.thead).map((row) =>
      filterNullArray(row),
    ),
    tbody: filterNullArray(props.structuredTable.tbody).map((row) =>
      filterNullArray(row),
    ),
    tfoot: filterNullArray(props.structuredTable.tfoot).map((row) =>
      filterNullArray(row),
    ),
  }
})

defineOptions({
  name: 'ParagraphTable',
})
</script>
